import { ChangeEvent, useCallback, useEffect, useState } from "react";
import clsx from "clsx";

// Interfaces
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";
import { IContentInformation, IExportDetails, IJournalResponse, IUrlResponse } from "./CitExport/interfaces";

// API
import { exportCitActivities } from "@DataPages/api/financeManagement/cit/mutations";
import { uploadCit } from "@DataPages/api/financeManagement/cit";

// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useMutation } from "@tanstack/react-query";
import { useCitContextData } from "@/DataPages/context/cit/CitContext";

// Helpers
import { errorLogger } from "@/Common/helpers/logger";
import { getExportableDetails, getOptionsDetails } from "./CitExport/helpers";

// MUI
import { Grid, Paper, Skeleton, Typography } from "@mui/material";

// Layouts
import ControlledDialog from "@/Layouts/components/Dialogs/ControlledDialog";
import UploadDataButton from "@/Layouts/components/Buttons/UploadDataButton";

// Components
import CitExport from "./CitExport";
import CitProcessOptions, { MarkAsExported } from "./CitExport/CitProcessOptions";
import BranchSelector from "@/DataPages/components/common/BranchSelector";

// Constants
import { EXPORT_PROCESS_TYPES, PROCESS_TYPES } from "./CitExport/CitProcessOptions/helper";
import actions from "@/Constants/actionsList";

interface ICitModelHeaderProps {
	selected: IPeriodTreeSelection;
	locationId?: string;
	isUpdatable: boolean;
	isLoading: boolean;
	isFetching?: boolean;
}

export const CitModelHeader = (props: Readonly<ICitModelHeaderProps>) => {
	const { selected, locationId, isUpdatable, isLoading = false, isFetching = false } = props ?? {};
	const { exportHistory, receivables } = useCitContextData();

	//States
	const [ingesting, setIngesting] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [exportDetails, setExportDetails] = useState<IExportDetails>({
		processType: "csv",
		options: [],
		markAsExported: false,
		storeBranch: "",
	});

	// Permissions
	const { useAuth: isAuthorized } = useAuthorization();
	const CAN_UPLOAD_CIT_REPORT = isAuthorized(actions.UPLOAD_CIT_REPORT);
	const CAN_EXPORT_WRITE_OFF = isAuthorized(actions.EXPORT_WRITE_OFF);

	// Mutations
	const { mutate: sendExport } = useMutation<
		void,
		Error,
		{ options: Partial<IContentInformation>[]; processType: string; markAsExported: boolean; storeBranch: string }
	>({
		mutationFn: async (input) => {
			if (!selected || !locationId) {
				throw new Error("No selected value");
			}

			setExporting(true);
			const exportedResult = await exportCitActivities({
				locationId,
				input,
				commissionCycleId: selected.cycleId,
				payPeriodId: selected.payPeriodId,
			});

			if (exportedResult?.urlDetails) {
				const details = exportedResult?.urlDetails;
				const { error } = details;
				if (!error) {
					downloadCsv(exportedResult.urlDetails);
				}
			} else {
				reportJournalResponse(exportedResult?.journalDetails);
			}

			return exportedResult;
		},
		onSuccess: () => {
			setExporting(false);
		},
	});

	const reportJournalResponse = useCallback(async (args: IJournalResponse) => {
		const { success: wasSuccessful = false } = args ?? {};

		if (wasSuccessful) {
			console.log("Journal entry posted successfully.");
		} else {
			console.log("There was an error posting the journal entry.");
		}

		setExporting(false);
		return null;
	}, []);

	const downloadCsv = useCallback(async (args: IUrlResponse) => {
		const { fileName = "", presignedUrl = "" } = args ?? {};
		if (!presignedUrl) {
			return;
		}

		const link = document.createElement("a");
		link.href = presignedUrl;
		link.download = fileName;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		setExporting(false);
	}, []);

	const handleUploadFile = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0] || null;

		if (!file) {
			return;
		}

		try {
			if (file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
				if (locationId && selected?.cycleId && selected?.payPeriodId) {
					const upload = uploadCit({
						commissionCycleId: selected.cycleId,
						payPeriodId: selected.payPeriodId,
						locationId,
						file,
					});
					upload.then(() => {
						event.target.files = null;
						setIngesting(false);
					});
				}

				setIngesting(true);
			}
		} catch (error) {
			errorLogger(error as Error);
		}
	};

	const handleBranchSelection = (branch: string) => {
		setExportDetails((prev) => ({ ...prev, storeBranch: branch }));
	};

	const handleExport = () => {
		const inputOptions = getExportableDetails(exportDetails?.options ?? []);
		sendExport({ ...exportDetails, options: inputOptions });
	};

	const handleUpdateExportType = (processType: string) => {
		const isJournal = processType === PROCESS_TYPES.JOURNAL;
		setExportDetails((prev) => ({ ...prev, processType, markAsExported: isJournal }));
	};

	const handleUpdateMarkAsExported = () => {
		setExportDetails((prev) => ({ ...prev, markAsExported: !prev.markAsExported }));
	};

	const handleUpdateExportOptions = (options: IContentInformation[]) => {
		setExportDetails((prev) => ({ ...prev, options }));
	};

	useEffect(() => {
		return () => {
			setIngesting(false);
			setExporting(false);
		};
	}, []);

	const defaultExportOptions = useCallback(() => {
		const optionDetails = getOptionsDetails(receivables ?? []);
		setExportDetails((prev) => ({ ...prev, processType: "csv", options: optionDetails, markAsExported: false }));
	}, [receivables]);

	useEffect(() => {
		defaultExportOptions();
		return () => {
			setExportDetails({
				processType: "csv",
				options: [],
				markAsExported: false,
				storeBranch: "",
			});
		};
	}, [receivables]);

	const exportableOptions = getExportableDetails(exportDetails?.options ?? []);
	const isLoadingExport = exporting || isFetching;
	const isExportDisabled = !CAN_EXPORT_WRITE_OFF || isLoadingExport || !isUpdatable;
	const isUploadReportDisabled = !CAN_UPLOAD_CIT_REPORT || isLoadingExport || !isUpdatable;
	const isActionExportDisabled = isExportDisabled || !exportableOptions?.length || exportDetails.storeBranch === "";
	const isMarkAsExportedDisabled = exportDetails.processType === PROCESS_TYPES.JOURNAL;

	return (
		<Grid item xs={12}>
			{isLoading ? (
				<Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
					<Grid item>
						<Skeleton variant="rectangular" height={25} width={350} className="rounded-md" />
					</Grid>

					<Grid item className="pr-0">
						<Grid container direction="row" justifyContent="space-between" spacing={4}>
							<Grid item>
								<Skeleton variant="rectangular" height={30} width={120} className="rounded-md" />
							</Grid>
							<Grid item>
								<Skeleton variant="rectangular" height={30} width={120} className="rounded-md" />
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			) : (
				<>
					{selected && (
						<Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
							<Grid item>
								<Typography className="!font-montserrat !font-medium !text-[24px]/[1.2] text-default" component="div">
									{selected?.titleExtended} Contracts In Transit
								</Typography>
							</Grid>
							<Grid item className="pr-0">
								<Grid container direction="row" justifyContent="space-between" spacing={4}>
									<Grid item>
										<ControlledDialog
											title={isLoadingExport ? "" : "Preview Export"}
											disabled={isExportDisabled}
											fetching={isLoadingExport}
											classes={clsx({
												"!text-white !min-w-[120px] [&_.MuiLoadingButton-loadingIndicator]:!text-white": true,
												"!bg-primary hover:!shadow-primary-shadow-50": !isExportDisabled,
												"!bg-disabled": isExportDisabled,
											})}
											onClickAction={() => setExportDetails((prev) => ({ ...prev, storeBranch: "" }))}
											dialogProperties={{
												title: "Write-Off Export Preview",
												actionComponent: (
													<MarkAsExported
														isDisabled={isMarkAsExportedDisabled}
														isChecked={exportDetails.markAsExported}
														onChange={handleUpdateMarkAsExported}
													>
														<CitProcessOptions
															defaultOption={"csv"}
															options={EXPORT_PROCESS_TYPES}
															onProcessChange={handleUpdateExportType}
														/>
													</MarkAsExported>
												),
												actions: [
													{
														title: "Cancel",
														shouldCloseDialog: true,
														classes: "!text-white !bg-gray-blue !shadow-none hover:!shadow-gray-blue-shadow-50",
														onClickAction: () => defaultExportOptions(),
													},
													{
														title: "Export",
														shouldCloseDialog: true,
														disabled: isActionExportDisabled,
														classes: clsx({
															"!text-white": true,
															"!bg-primary hover:!shadow-primary-shadow-50": !isActionExportDisabled,
															"!bg-disabled": isActionExportDisabled,
														}),
														onClickAction: handleExport,
													},
												],
											}}
											content={
												<>
													<BranchSelector
														locationId={locationId ?? ""}
														selectedBranch={exportDetails.storeBranch}
														onChange={handleBranchSelection}
														className="!w-[218px] !ml-[950px]"
													/>
													<CitExport
														activities={receivables}
														exportHistory={exportHistory ?? []}
														optionsDetails={exportDetails?.options ?? []}
														onActionUpdate={handleUpdateExportOptions}
													/>
												</>
											}
											onCloseAction={() => defaultExportOptions()}
										/>
									</Grid>
									<Grid item>
										<Paper elevation={0}>
											<UploadDataButton
												title="Upload Report"
												className={clsx(
													`hover:!shadow-primary-shadow-50 !h-[30px] !rounded-md [&.Mui-disabled]:opacity-100 [&.Mui-disabled]:!text-white !px-[18px] !py-[6px] z-10\
															[&_.MuiTypography-root]:!font-sans [&_.MuiTypography-root]:!font-normal [&_.MuiTypography-root]:!text-sm/[normal] min-w-[120px]`,
													{
														"!bg-primary ": !isUploadReportDisabled,
														"!bg-disabled": isUploadReportDisabled,
													}
												)}
												accept=".xlsx"
												disabled={isUploadReportDisabled}
												uploading={ingesting}
												onChange={handleUploadFile}
											/>
										</Paper>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					)}
				</>
			)}
		</Grid>
	);
};
